import { action, computed, observable, makeObservable } from 'mobx'
import area from '@turf/area'
import _ from 'lodash'
import XlsxPopulate from 'xlsx-populate/browser/xlsx-populate'
import { ageBuckets, strings, selectionTypes } from 'constants.js'
import { downloadExcel, downloadCSV, sheetFromArrays } from 'utils'
import { dataStore, selectionStore } from './UIStore'
import api from '../api'

// Statistics for a geopolygon, admin boundary, or target group
class RegionAnalysis {
  @observable sum
  @observable male
  @observable female
  @observable area
  @observable density
  @observable pyramids
  @observable markersMode
  @observable markersList = []
  @observable selectedMarkerId
  @observable loadingMarkers
  @observable hh_size = 0
  @observable hh_income = 0
  @observable.ref geoJSON
  @observable loading = true
  @observable hoveredPyramid = null
  @observable avgAge
  @observable genderRatio

  constructor () {
    makeObservable(this)
    this.markersMode = false
  }

  @computed get maxValue () {
    try {
      return _(this.pyramids)
        .values()
        .map(pyramid => _.maxBy(pyramid, 'value').value)
        .max()
    } catch (e) {
      console.log(e)
    }
  }

  isValid (data) {
    return !_.isNil(data) &&
      _.every(data.Pyramids, (pyramid) => (
        !_.isNil(pyramid.AgeGroup) &&
        !_.isNil(pyramid.TotalPop) &&
        !_.isNil(pyramid.FemalePop) &&
        !_.isNil(pyramid.MalePop)
      ))
  }

  @action toggleAnalysisType (markersMode) {
    this.markersMode = markersMode
  }

  @action load (data, geoJSON) {
    this.geoJSON = geoJSON

    if (this.isValid(data)) {
      this.pyramids = {
        total: _.map(data.Pyramids, pyramid => ({
          age: pyramid.AgeGroup,
          gender: 'total',
          value: pyramid.TotalPop
        })),

        female: _.map(data.Pyramids, pyramid => ({
          age: pyramid.AgeGroup,
          gender: 'female',
          value: pyramid.FemalePop
        })),

        male: _.map(data.Pyramids, pyramid => ({
          age: pyramid.AgeGroup,
          gender: 'male',
          value: pyramid.MalePop
        }))
      }

      const geopolygonArea = this.geoJSON ? (area(this.geoJSON) / 1000000) : null

      this.male = data['male']
      this.female = data['female']
      this.sum = data['sum'] != null ? data['sum'] : (data['sum_pop'] != null ? data['sum_pop'] : (data['total_population'] != null ? data['total_population'] : (data['population'] != null ? data['population'] : (data['value'] != null ? data['value'] : null))))
      this.area = geopolygonArea
      const totalPop = this.sum
      this.density = geopolygonArea && geopolygonArea > 0 && totalPop != null ? (totalPop / geopolygonArea) : null
      this.hh_size = (data['hh_size'] !== undefined && data['hh_size'] !== null && !isNaN(Number(data['hh_size']))) ? Number(data['hh_size']) : null
      this.hh_income = (data['hh_income'] !== undefined && data['hh_income'] !== null && !isNaN(Number(data['hh_income']))) ? Number(data['hh_income']) : null
      this.avgAge = this.calculateAverageAge()
      this.genderRatio = this.calculateGenderRatio()
      this.valid = true
    } else {
      this.valid = false
    }

    this.loading = false
  }

  async loadMarkersAnalysis () {
    this.loadingMarkers = true
    let shape_type, id
    switch (selectionStore.type) {
      case selectionTypes.GEOPOLYGON:
        shape_type = 'geopolygon'
        id = selectionStore.item.id
        break
      case selectionTypes.BOUNDARY:
        shape_type = 'boundary'
        id = selectionStore.item.inner_id
        break
      case selectionTypes.TARGET_GROUP:
        shape_type = 'target_group'
        id = selectionStore.item.pk
        break
    }

    if (shape_type) {
      const result = await api.postFetchMarkersFallWithin({ shape_type, id })
      _.each(result.data.results, item => {
        const group = _.find(dataStore.targetGroups, { pk: item.target_group })
        const marker = _.find(group.savedMarkers, { id: item.id })
        this.markersList.push(marker)
      })
    }
    this.loadingMarkers = false
  }

  async exportExcel (name, entity) {
    const target = entity || selectionStore.item
    if (!target) return

    const workbook = await XlsxPopulate.fromBlankAsync()
    const sheetNames = new Map()
    const rootSheet = await this.createExcelSheet(
      workbook,
      target,
      this.getUniqueSheetName(target, sheetNames)
    )
    // Build simple 3-column rows: [Location, Name, Total Population]
    const summaryRows = []
    const getValues = function (arr) {
      return _.map(arr, function (o) { return _.round(o.value) })
    }
    function sumPyramid (analysis) {
      if (!analysis || !analysis.pyramids || !analysis.pyramids.total) return ''
      const totalVals = getValues(analysis.pyramids.total)
      return _.sum(totalVals)
    }
    const isTargetGroup = !!(target.geopolygons && target.boundaries && target.zipCodeBoundaries)

    // created sheet for each geometry if target group selected
    if (isTargetGroup) {
      // Folder (parent) row
      summaryRows.push(['Folder', target.name, sumPyramid(target.analysis || this)])

      const geoRows = await Promise.all(
        target.geopolygons.map(async (item, i) => {
          if (!item.pk && typeof item.loadGeopolygonData === 'function') {
            await item.loadGeopolygonData()
          }
          const uniqueName = this.getUniqueSheetName(item, sheetNames)
          await this.createExcelSheet(workbook, item, uniqueName)
          return [`Item ${i + 1}`, item.name, sumPyramid(item.analysis)]
        })
      )
      // Boundaries (in order, continue numbering)
      const boundaryRows = await Promise.all(
        target.boundaries.map(async (item, i) => {
          if (!item.pk && typeof item.loadAnalysis === 'function') {
            await item.loadAnalysis()
          }
          const uniqueName = this.getUniqueSheetName(item, sheetNames)
          await this.createExcelSheet(workbook, item, uniqueName)
          return [`Item ${geoRows.length + i + 1}`, item.name, sumPyramid(item.analysis)]
        })
      )
      // Zip codes are stored separately from admin boundaries but use the same Boundary model / export path.
      const zipCodeBoundaryRows = await Promise.all(
        target.zipCodeBoundaries.map(async (item, i) => {
          if (!item.pk && typeof item.loadAnalysis === 'function') {
            await item.loadAnalysis()
          }
          const uniqueName = this.getUniqueSheetName(item, sheetNames)
          await this.createExcelSheet(workbook, item, uniqueName)
          const itemIndex = geoRows.length + boundaryRows.length + i + 1
          return [`Item ${itemIndex}`, item.name, sumPyramid(item.analysis)]
        })
      )

      summaryRows.push(...geoRows)
      summaryRows.push(...boundaryRows)
      summaryRows.push(...zipCodeBoundaryRows)
    }
    // Append the 3-column table (no styling) at the end of the first sheet
    if (rootSheet && summaryRows.length) {
      const used = rootSheet.usedRange()
      const startRow = (used ? used.endCell().rowNumber() : 0) + 1
      const table = [
        ['Location', 'Name', 'Total Population'],
        ...summaryRows
      ]
      rootSheet.cell(startRow, 1).value(table)
    }
    workbook.sheet(0).delete()
    downloadExcel(name, workbook)
  }

  async exportCSV (name, currentItem = null) {
    const target = currentItem || selectionStore.item
    if (!target) return

    const sheetGeoNames = new Map()
    const sheetBoundaryNames = new Map()
    const allRows = []

    const rootRows = this.createCSVRows(target)
    allRows.push(...rootRows)
    allRows.push([])

    // Build simple 3-column rows: [Location, Name, Total Population]
    const summaryRows = []
    const getValues = function (arr) {
      return _.map(arr, function (o) { return _.round(o.value) })
    }
    function sumPyramid (analysis) {
      if (!analysis || !analysis.pyramids || !analysis.pyramids.total) return ''
      const totalVals = getValues(analysis.pyramids.total)
      return _.sum(totalVals)
    }

    // Folder (parent) row
    summaryRows.push(['Folder', target.name || name, sumPyramid(target.analysis || this)])

    const isTargetGroup = (selectionStore.type === selectionTypes.TARGET_GROUP && selectionStore.item === target) || (target.geopolygons && Array.isArray(target.geopolygons))

    if (isTargetGroup) {
      if (target.geopolygons && target.geopolygons.length > 0) {
        const geoRows = await Promise.all(
          target.geopolygons.map(async (item, i) => {
            if (!item.pk && typeof item.loadGeopolygonData === 'function') {
              await item.loadGeopolygonData()
            }
            const uniqueName = this.getUniqueSheetName(item, sheetGeoNames)
            const rows = this.createCSVRows(item, uniqueName)
            allRows.push(...rows)
            allRows.push([])
            return [`Item ${i + 1}`, item.name, sumPyramid(item.analysis)]
          })
        )
        summaryRows.push(...geoRows)
      }

      if (target.boundaries && target.boundaries.length > 0) {
        const boundaryOffset = summaryRows.length - 1
        const boundaryRows = await Promise.all(
          target.boundaries.map(async (item, i) => {
            if (!item.pk && typeof item.loadAnalysis === 'function') {
              await item.loadAnalysis()
            }
            const uniqueName = this.getUniqueSheetName(item, sheetBoundaryNames)
            const rows = this.createCSVRows(item, uniqueName)
            allRows.push(...rows)
            allRows.push([])
            return [`Item ${boundaryOffset + i + 1}`, item.name, sumPyramid(item.analysis)]
          })
        )
        summaryRows.push(...boundaryRows)
      }

      if (target.zipCodeBoundaries && target.zipCodeBoundaries.length > 0) {
        const zipOffset = summaryRows.length - 1
        const zipCodeBoundaryRows = await Promise.all(
          target.zipCodeBoundaries.map(async (item, i) => {
            if (!item.pk && typeof item.loadAnalysis === 'function') {
              await item.loadAnalysis()
            }
            const uniqueName = this.getUniqueSheetName(item, sheetBoundaryNames)
            const rows = this.createCSVRows(item, uniqueName)
            allRows.push(...rows)
            allRows.push([])
            return [`Item ${zipOffset + i + 1}`, item.name, sumPyramid(item.analysis)]
          })
        )
        summaryRows.push(...zipCodeBoundaryRows)
      }
    }

    if (summaryRows.length > 0) {
      allRows.push(['Location', 'Name', 'Total Population'])
      summaryRows.forEach(row => allRows.push(row))
    }

    const escapeCell = (val) => {
      if (val === null || val === undefined) return ''
      const str = String(val)
      // Prevent Excel from auto-converting age brackets like 5-9 or 10-14 to dates (e.g. 9-May, 14-Oct)
      if (/^\d+-\d+(\+)?$/.test(str)) {
        return `="${str}"`
      }
      if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`
      }
      return str
    }

    const csvContent = allRows.map(row => row.map(escapeCell).join(',')).join('\n')
    downloadCSV(name || target.name || 'export', csvContent)
  }

  createCSVRows (geometry, name) {
    const sheetName = name || (geometry && geometry.name) || ''
    const getValues = (arr) => {
      return _.map(arr, ({ value }) => _.round(value))
    }
    const analysis = geometry && geometry.analysis
    const rows = []

    rows.push([sheetName])

    if (analysis && analysis.pyramids && analysis.pyramids.total) {
      const totalValues = getValues(analysis.pyramids.total)
      const femaleValues = getValues(analysis.pyramids.female)
      const maleValues = getValues(analysis.pyramids.male)

      rows.push([
        '',
        strings.POPULATION.TOTAL,
        ...Array.from(ageBuckets.values())
      ])
      rows.push([
        strings.POPULATION.POPULATION,
        _.sum(totalValues),
        ...totalValues
      ])
      rows.push([
        strings.POPULATION.FEMALE,
        _.sum(femaleValues),
        ...femaleValues
      ])
      rows.push([
        strings.POPULATION.MALE,
        _.sum(maleValues),
        ...maleValues
      ])
      rows.push([])
      rows.push([
        strings.POPULATION.AREA,
        strings.POPULATION.DENSITY
      ])
      rows.push([
        _.round(analysis.area || 0),
        _.round(analysis.density || 0)
      ])
    } else {
      rows.push(['Check the name of the your region and confirm it does not duplicate'])
    }

    return rows
  }

  getUniqueSheetName (item, sheetNamesMap) {
    const baseName = item.name.replace(/[\\/*[\]:?']/g, ' ').slice(0, 28)
    let uniqueName = baseName

    if (sheetNamesMap.has(uniqueName)) {
      const counter = sheetNamesMap.get(baseName)
      uniqueName = `${baseName}(${counter})`
      sheetNamesMap.set(baseName, counter + 1)
    } else {
      sheetNamesMap.set(uniqueName, 1)
    }

    return uniqueName
  }

  calculateAverageAge () {
    if (this.sum === 0) return 0

    let sum = 0
    for (const i of this.pyramids.total) {
      let age = null
      let averageAge = 0
      if (!i.age.includes('plus')) {
        age = i.age.split('_')
        averageAge = ((+age[0]) + (+age[1])) / 2
      } else {
        age = i.age.split('plus')
        averageAge = +age[0]
      }
      sum += (i.value * averageAge)
    }

    return (sum / this.sum)
  }

  calculateGenderRatio () {
    if (this.female === 0) {
      return 0
    }

    return (this.male / this.female)
  }

  async createExcelSheet (workbook, geometry, name) {
    const newSheetName = name || geometry.name

    const getValues = (arr) => {
      return _.map(arr, ({ value }) => _.round(value))
    }

    if (!geometry.pk) {
      if (typeof geometry.loadGeopolygonData === 'function') {
        await geometry.loadGeopolygonData()
      }
    }

    const analysis = geometry.analysis

    if (analysis && analysis.pyramids && analysis.pyramids.total) {
      const totalValues = getValues(analysis.pyramids.total)
      const femaleValues = getValues(analysis.pyramids.female)
      const maleValues = getValues(analysis.pyramids.male)

      const sheet = sheetFromArrays(workbook, newSheetName, [
        [
          '',
          strings.POPULATION.TOTAL,
          ...Array.from(ageBuckets.values())
        ],
        [
          strings.POPULATION.POPULATION,
          _.sum(totalValues),
          ...totalValues
        ],
        [
          strings.POPULATION.FEMALE,
          _.sum(femaleValues),
          ...femaleValues
        ],
        [
          strings.POPULATION.MALE,
          _.sum(maleValues),
          ...maleValues
        ],
        [],
        [
          strings.POPULATION.AREA,
          strings.POPULATION.DENSITY
        ],
        [
          _.round(analysis.area),
          _.round(analysis.density)
        ]
      ])

      const endColumn = 2 + analysis.pyramids.length

      sheet.range(1, 1, 1, endColumn).style({
        horizontalAlignment: 'right',
        bold: true
      })

      sheet.range(1, 1, 4, 1).style('bold', true)
      sheet.range(6, 1, 6, 2).style('bold', true)
      sheet.range(2, 2, 4, endColumn)

      return sheet
    } else {
      const err_sheet = workbook.addSheet(`Error ${geometry.id} + ${_.uniqueId()}`)
      err_sheet.cell('A1').value(geometry.name.replace(/[\\/*[\]:?]/g, ' ').slice(0, 31))
      err_sheet.cell('A2').value('No population data is available for this region yet.')
      err_sheet.cell('A3').value('Open the region to run its analysis, then export again.')

      return err_sheet
    }
  }
}

export default RegionAnalysis
